import extractDomain from 'extract-domain'
import clsx from 'clsx'
import { PopOver, Tooltip } from '@pepper/ui'

import GoogleIntegrationButton from 'components/GoogleIntegrationButton'

import TopSearchLoader from '../TopSearchLoader'
import TopSearchResult from '../TopSearchResult'
import Header from '../Header'

import { getPrevMonthDuration } from 'utils/functions/IdeaLab/getPrevMonthDuration'

import GoogleIcon from 'assets/images/google_logo.svg'

import {
  KeywordLabel,
  KeywordSelfRankingPage,
  KeywordTopRankingDetails,
} from 'types/keyword'

import s from './KeywordDetailsPopover.module.css'

const RESULTS_COUNT = 3

type Props = {
  companyId: string
  isDomainRemoved: boolean
  children: React.ReactNode
  primaryKeyword: string
  isLoading: boolean
  isGoogleConnected: boolean
  onGetKeywordLabel: () => void
  keywordLabel?: KeywordLabel
  topSearchResults?: KeywordTopRankingDetails[] | null
  selfRankingPages?: KeywordSelfRankingPage[] | null
  domain?: string
  updatedAt?: string
}

export const KeywordDetailsPopover = ({
  companyId,
  children,
  primaryKeyword,
  isLoading,
  isGoogleConnected,
  onGetKeywordLabel,
  keywordLabel,
  topSearchResults,
  selfRankingPages,
  domain,
  updatedAt,
  isDomainRemoved,
}: Props) => {
  const firstDay =
    !!updatedAt && getPrevMonthDuration(new Date(updatedAt)).firstDay
  const lastDay =
    !!updatedAt && getPrevMonthDuration(new Date(updatedAt)).lastDay

  return isDomainRemoved ? (
    <Tooltip
      title="Domain associated with this project has been removed"
      placement="top"
    >
      <div className={clsx(s.trigger, s.disabled)}>{children}</div>
    </Tooltip>
  ) : (
    <PopOver
      placement="bottom"
      overlayClassName={s.overlay}
      trigger="click"
      onVisibleChange={onGetKeywordLabel}
      content={
        <div className={s.wrapper}>
          <Header
            isLoading={isLoading}
            label={primaryKeyword}
            difficulty={keywordLabel?.difficulty}
            volume={keywordLabel?.volume}
          />
          <div className={s.topResults}>
            <div className={s.header}>
              <img src={GoogleIcon} width={16} alt="google-logo" />
              <div className={s.headerText}>Top Search Results</div>
            </div>
            <div className={s.list}>
              {isLoading && (
                <>
                  <TopSearchLoader />
                  <TopSearchLoader />
                  <TopSearchLoader />
                </>
              )}
              {!isLoading &&
                topSearchResults &&
                topSearchResults.slice(0, RESULTS_COUNT)?.map(result => (
                  <div key={result.url} className={s.result}>
                    <div className={s.positionIndicator}>
                      #{Math.round(result.position)}
                    </div>
                    <TopSearchResult
                      url={result.url}
                      displayUrl={result.displayed_url}
                      item={result}
                      logo={result.favicon}
                    />
                  </div>
                ))}
              {!isLoading && !topSearchResults && (
                <div className={s.noItems}>
                  <span className={s.positionIndicator}>--</span>
                  No ranking pages for this keyword
                </div>
              )}
            </div>
          </div>
          {isGoogleConnected ? (
            <div className={s.yourPages}>
              <div className={s.header}>
                Your Pages
                {firstDay && lastDay && (
                  <div className={s.duration}>
                    ({firstDay} - {lastDay})
                  </div>
                )}
              </div>
              <div className={s.list}>
                {isLoading && <TopSearchLoader isSelfRankingPages={true} />}
                {!isLoading &&
                  selfRankingPages &&
                  selfRankingPages.slice(0, RESULTS_COUNT)?.map(result => (
                    <div key={result.url} className={s.result}>
                      <div
                        className={clsx(
                          s.positionIndicator,
                          s.yourPagesPositionIndicator
                        )}
                      >
                        #{Math.round(result.position)}
                      </div>
                      <TopSearchResult
                        url={result.url}
                        displayUrl={result.url}
                        item={result}
                        logo={`https://logo.clearbit.com/${extractDomain(
                          domain ?? ''
                        )}`}
                      />
                    </div>
                  ))}
                {!isLoading && !selfRankingPages && (
                  <div className={s.noItems}>
                    <span className={s.positionIndicator}>--</span>
                    No ranking pages for this keyword
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={s.linkGoogleWrapper}>
              <div className={s.linkGoogleLabel}>
                Connect account to see your ranking pages
              </div>
              <GoogleIntegrationButton
                type="ghost"
                companyId={companyId}
                size="middle"
              />
            </div>
          )}
        </div>
      }
    >
      <button className={s.trigger} onClick={onGetKeywordLabel}>
        {children}
      </button>
    </PopOver>
  )
}
